/** Poll a queued Ace stability test and surface in-app results. */

import { useEffect, useRef } from "react";
import { agentReviewAPI, type AceStabilityTestResult } from "@/services/api/agentReview";
import { listTasks, type TaskStatusResponse } from "@/services/api/taskManager";
import { shouldAutoRequeueStability } from "./aceStabilityResultHelpers";

export type BackgroundAceStabilityState = {
  taskId: string;
  caseLabel: string;
  message: string;
  progress: number | null;
  startedAt: number;
  /** When the worker actually started this task (not queue wait time). */
  executionStartedAt: number | null;
  iterations: number;
  maxWaitMs: number;
  queuedBehindAnother?: boolean;
  blockingTaskLabel?: string | null;
  /** Queued with no other Ace job running — waiting for worker pickup. */
  waitingForWorker?: boolean;
  /** Auto or manual requeue was attempted for a stuck queued task. */
  republishAttempted?: boolean;
  /** Client stopped waiting for UI updates, but the server task may still be running. */
  trackingPaused?: boolean;
  result: AceStabilityTestResult | null;
  error: string | null;
};

const STORAGE_KEY = "agent-review-background-ace-stability";
const POLL_MS = 2_000;
const TASK_TYPE = "agent_review_ace_stability_test";

const BLOCKING_TASK_TYPES = [
  TASK_TYPE,
  "agent_review_ace_ambiguity_export",
  "agent_review_ace_comparison_export",
];

export function loadPersistedAceStability(): BackgroundAceStabilityState | null {
  if (typeof sessionStorage === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as BackgroundAceStabilityState;
    if (!parsed?.taskId) return null;
    if (parsed.error?.includes("Task Manager")) {
      return { ...parsed, error: null, trackingPaused: true };
    }
    return parsed;
  } catch {
    return null;
  }
}

export function persistAceStability(state: BackgroundAceStabilityState | null): void {
  if (typeof sessionStorage === "undefined") return;
  if (!state) {
    sessionStorage.removeItem(STORAGE_KEY);
    return;
  }
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

function formatBlockingTaskLabel(task: TaskStatusResponse): string {
  if (task.task_type.includes("stability")) return "Ace stability test";
  if (task.task_type.includes("ambiguity")) return "Ace ambiguity export";
  if (task.task_type.includes("comparison")) return "Ace comparison export";
  return task.task_type;
}

async function findBlockingAceReviewTask(
  excludeTaskId: string,
): Promise<{ taskId: string; label: string } | null> {
  try {
    const { tasks } = await listTasks({
      status: ["running"],
      task_type: BLOCKING_TASK_TYPES,
      limit: 10,
    });
    const blocker = tasks.find((task) => task.task_id !== excludeTaskId);
    if (!blocker) return null;
    return { taskId: blocker.task_id, label: formatBlockingTaskLabel(blocker) };
  } catch {
    return null;
  }
}

function isRunningStatus(status: string): boolean {
  return status === "running" || status === "processing";
}

type UseAceStabilityTestOptions = {
  state: BackgroundAceStabilityState | null;
  onUpdate: (patch: Partial<BackgroundAceStabilityState>) => void;
  onComplete: (result: AceStabilityTestResult) => void;
  onError: (message: string) => void;
};

export function useAceStabilityTest({
  state,
  onUpdate,
  onComplete,
  onError,
}: UseAceStabilityTestOptions): void {
  const onUpdateRef = useRef(onUpdate);
  const onCompleteRef = useRef(onComplete);
  const onErrorRef = useRef(onError);
  onUpdateRef.current = onUpdate;
  onCompleteRef.current = onComplete;
  onErrorRef.current = onError;

  useEffect(() => {
    if (!state?.taskId || state.result || state.error) return;

    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let executionStartedAt = state.executionStartedAt;
    let republishAttempted = state.republishAttempted ?? false;

    const schedule = (delay: number) => {
      timeoutId = setTimeout(() => {
        void tick();
      }, delay);
    };

    const tick = async () => {
      if (cancelled || !state) return;

      try {
        const taskId = state.taskId;
        const taskStatus = await agentReviewAPI.getAceStabilityTestStatus(taskId);
        if (cancelled) return;

        const running = isRunningStatus(taskStatus.status);
        if (running && executionStartedAt == null) {
          executionStartedAt = Date.now();
        }

        if (taskStatus.status === "queued") {
          const blocker = await findBlockingAceReviewTask(taskId);
          if (cancelled) return;

          const queuedBehindAnother = Boolean(blocker);
          const waitingForWorker = !queuedBehindAnother;
          const queueWaitMs = Date.now() - state.startedAt;

          if (
            shouldAutoRequeueStability({
              waitingForWorker,
              republishAttempted,
              queueWaitMs,
            })
          ) {
            try {
              await agentReviewAPI.requeueAceStabilityTest(taskId);
              republishAttempted = true;
              onUpdateRef.current({
                message: "Re-queued — waiting for worker to pick up the job…",
                progress: taskStatus.progress ?? null,
                queuedBehindAnother: false,
                blockingTaskLabel: null,
                waitingForWorker: true,
                republishAttempted: true,
                trackingPaused: false,
              });
              schedule(POLL_MS);
              return;
            } catch {
              republishAttempted = true;
            }
          }

          onUpdateRef.current({
            message: taskStatus.status_message ?? "Waiting in queue…",
            progress: taskStatus.progress ?? null,
            queuedBehindAnother,
            blockingTaskLabel: blocker?.label ?? null,
            waitingForWorker,
            republishAttempted,
            trackingPaused: false,
          });
        } else {
          onUpdateRef.current({
            message: taskStatus.status_message ?? taskStatus.status,
            progress: taskStatus.progress ?? null,
            queuedBehindAnother: false,
            blockingTaskLabel: null,
            waitingForWorker: false,
            executionStartedAt,
          });

          if (
            running &&
            executionStartedAt != null &&
            Date.now() - executionStartedAt > state.maxWaitMs &&
            !state.trackingPaused
          ) {
            onUpdateRef.current({
              trackingPaused: true,
              executionStartedAt,
            });
          }
        }

        if (taskStatus.status === "failed" || taskStatus.error) {
          onErrorRef.current(taskStatus.error ?? "Ace stability test failed");
          return;
        }

        if (taskStatus.ready && taskStatus.result) {
          onUpdateRef.current({
            result: taskStatus.result,
            trackingPaused: false,
            executionStartedAt,
            waitingForWorker: false,
          });
          if (!cancelled) onCompleteRef.current(taskStatus.result);
          return;
        }

        if (taskStatus.status === "cancelled") {
          onErrorRef.current("Ace stability test was cancelled");
          return;
        }

        schedule(POLL_MS);
      } catch (err) {
        if (!cancelled) {
          onErrorRef.current(err instanceof Error ? err.message : "Ace stability test failed");
        }
      }
    };

    void tick();

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [
    state?.taskId,
    state?.result,
    state?.error,
    state?.executionStartedAt,
    state?.maxWaitMs,
    state?.trackingPaused,
    state?.republishAttempted,
    state?.startedAt,
  ]);
}

export async function requeueAceStabilityTestTask(taskId: string): Promise<void> {
  await agentReviewAPI.requeueAceStabilityTest(taskId);
}
